import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

// =============================================================================
// Response shape primitives — every list endpoint returns { items, metadata }
// per CLAUDE.md API standards. Single-resource endpoints return { data, metadata }.
// =============================================================================

export interface ResponseMetadata {
  total?: number;
  page?: number;
  pageSize?: number;
  timestamp: string;
  requestId?: string;
}

export interface ListResponse<T> {
  items: T[];
  metadata: ResponseMetadata;
}

export interface SingleResponse<T> {
  data: T;
  metadata: ResponseMetadata;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface SortingParams {
  /** column id from the per-endpoint sort allowlist */
  sortBy?: string;
  /** 'asc' | 'desc'. Defaults server-side to 'asc' if omitted. */
  sortDir?: 'asc' | 'desc';
}

// =============================================================================
// Per-endpoint response interfaces. Each mirrors the API DTO shape produced by
// TaxReadApiHandler. Field names match the API; convert from snake_case to
// camelCase at the consumer layer if a view prefers camelCase.
// =============================================================================

export interface TaxYearsResponse {
  data: number[];
  metadata: ResponseMetadata;
}

export interface PortfolioOverviewItem {
  portfolio_id: string;
  display_name: string;
  environment: string;
  coinbase_transaction_count: number;
  system_trade_count: number;
  reconciled_count: number;
  tax_lot_count: number;
  cost_basis_count: number;
}

export interface CoinbaseTransactionItem {
  portfolio_id: string;
  coinbase_order_id: string;
  product_id: string;
  trade_side: string;
  order_status: string;
  created_time: string;
  last_fill_time: string | null;
  filled_size: string;
  filled_value: string;
  average_filled_price: string | null;
  total_fees: string;
  total_value_after_fees: string;
}

export interface SystemTradeItem {
  source_environment: string;
  client_order_id: string;
  portfolio_id: string;
  product_id: string;
  trade_side: string;
  status: string;
  trade_group_type: string;
  trade_group_id: string | null;
  executed_at: string | null;
  quantity: string;
  usd_value: string | null;
}

export interface ReconciliationItem {
  portfolio_id: string;
  coinbase_order_id: string;
  source_environment: string | null;
  system_client_order_id: string | null;
  product_id: string;
  trade_group_type: string | null;
  trade_group_id: string | null;
  match_status: 'MATCHED' | 'PARTIAL_MATCH' | 'UNMATCHED';
  match_reason: string | null;
  created_time: string;
}

export interface TaxLotItem {
  lot_id: number;
  portfolio_id: string;
  product_id: string;
  acquisition_source_type: 'COINBASE' | 'MANUAL';
  acquisition_order_id: string;
  acquisition_date: string;
  acquisition_cost: string;
  disposal_source_type: 'COINBASE' | 'MANUAL';
  disposal_order_id: string;
  disposal_date: string;
  proceeds: string;
  quantity: string;
  fees_acquisition: string;
  fees_disposal: string;
  holding_period: 'SHORT_TERM' | 'LONG_TERM';
  lot_source: string;
}

export interface CostBasisItem {
  result_id: number;
  portfolio_id: string;
  product_id: string;
  disposal_order_id: string;
  disposal_date: string;
  disposal_quantity: string;
  disposal_proceeds: string;
  acquisition_type: 'COINBASE' | 'MANUAL_UNRESOLVED' | 'NONE';
  acquisition_id: string | null;
  acquisition_date: string | null;
  acquisition_cost: string;
  gain_loss: string;
  holding_period: 'SHORT_TERM' | 'LONG_TERM';
  method: 'SPECIFIC_ID' | 'HIFO' | 'LIFO' | 'FIFO' | 'ZERO_BASIS_UNKNOWN';
  tax_year: number;
}

export interface MissingCostBasisItem {
  item_type: 'UNASSIGNED_DISPOSAL' | 'ZERO_COST_ACQUISITION';
  result_id: number;
  portfolio_id: string;
  product_id: string;
  occurrence_date: string;
  quantity: string;
  usd_value: string | null;
  source_order_id: string;
  source_type: 'COINBASE' | 'MANUAL';
  /** Coinbase acquisition order ID surfaced for ZERO_COST_ACQUISITION rows (REQ-012). */
  acquisition_order_id?: string;
}

// =============================================================================
// Manual entries types — request/response DTOs for manual transactions, manual
// tax lots, allocation queries, Coinbase corrections, and inbox resolutions.
// =============================================================================

// Manual Transaction types
export interface ManualTransactionBody {
  portfolioId: string;
  productId: string;
  tradeSide: 'BUY' | 'SELL';
  transactionDate: string; // ISO string
  quantity: string; // BigDecimal as string
  pricePerUnit: string;
  fees: string;
  description: string;
  sourceReference?: string;
}
export interface ManualTransactionUpdateBody extends Partial<ManualTransactionBody> {
  reason: string;
}
export interface ManualTransactionDeleteBody {
  reason: string;
}
export interface ManualTransaction {
  transaction_id: number;
  portfolio_id: string;
  product_id: string;
  trade_side: 'BUY' | 'SELL';
  transaction_date: string;
  quantity: string;
  price_per_unit: string;
  total_value: string;
  fees: string;
  description: string;
  source_reference?: string;
  deleted_at?: string;
  created_at: string;
  last_modified: string;
}

// Manual Tax Lot types
export type LotAction = 'CREATE_MANUAL_LOT' | 'LINK_COINBASE_TO_MANUAL' | 'LINK_MANUAL_TO_COINBASE';
export interface ManualTaxLotBody {
  lotAction: LotAction;
  portfolioId: string;
  quantity: string;
  reason: string;
  acquisitionTransactionId?: number;
  disposalTransactionId?: number;
  coinbaseOrderId?: string;
  manualTransactionId?: number;
}
export interface ManualTaxLotUpdateBody {
  quantity: string;
  reason: string;
}
export interface ManualTaxLotDeleteBody {
  reason: string;
}
export interface ManualTaxLot {
  lot_id: number;
  portfolio_id: string;
  product_id: string;
  acquisition_source_type: string;
  acquisition_order_id: string;
  acquisition_date: string;
  acquisition_cost: string;
  disposal_source_type: string;
  disposal_order_id: string;
  disposal_date: string;
  proceeds: string;
  quantity: string;
  fees_acquisition: string;
  fees_disposal: string;
  holding_period: string;
  lot_source: string;
  source_reference?: string;
  deleted_at?: string;
}

// Allocation type
export interface AllocationResponse {
  data: { allocatedQuantity: string; orderId: string };
  metadata: { timestamp: string; requestId: string };
}

// Coinbase Correction (audit log entry)
export interface CoinbaseCorrection {
  auditId: number;
  entityType: string;
  entityId: string;
  action: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  reason: string;
  createdAt: string;
}

// Inbox Resolution types
export interface InboxResolution {
  resolutionId: number;
  resultId: number;
  portfolioId: string;
  resolutionType: string;
  reason?: string;
  resolvedAt: string;
}

// Combined inbox-resolution payloads (REQ-003 / REQ-004 / REQ-005). Each variant
// is keyed by the inbox row's `item_type` and the chosen `resolution_type` —
// the backend atomically creates the downstream manual_transaction /
// manual_tax_lot and the inbox_resolution row in a single DB transaction.
// `resolvedQuantity` is required by the backend DB schema (NOT NULL on
// inbox_resolution.resolved_quantity). For MVP the operator always resolves
// the full quantity of the underlying disposal/acquisition; the frontend
// reads it from `MissingCostBasisItem.quantity` and forwards it verbatim.
// Partial-resolution support is forward-compat (backend column exists; UI
// path is a follow-up CTR).
export interface UnassignedDisposalResolution {
  portfolioId: string;
  sourceIdentifier: string;
  itemType: 'UNASSIGNED_DISPOSAL';
  resolutionType: 'MANUAL_TAX_LOT_CREATED';
  productId: string;
  transactionDate: string;
  quantity: string;
  pricePerUnit: string;
  reason: string;
  resolvedQuantity: string;
}
export interface ZeroCostAcquisitionResolution {
  portfolioId: string;
  sourceIdentifier: string;
  itemType: 'ZERO_COST_ACQUISITION';
  resolutionType: 'MANUAL_TRANSACTION_CREATED';
  productId: string;
  transactionDate: string;
  quantity: string;
  pricePerUnit: string;
  reason: string;
  resolvedQuantity: string;
}
export interface IntentionallyUnresolvedResolution {
  portfolioId: string;
  sourceIdentifier: string;
  itemType: 'UNASSIGNED_DISPOSAL' | 'ZERO_COST_ACQUISITION';
  resolutionType: 'INTENTIONALLY_UNRESOLVED';
  reason: string;
  resolvedQuantity: string;
}
export type CombinedInboxResolutionPayload =
  | UnassignedDisposalResolution
  | ZeroCostAcquisitionResolution
  | IntentionallyUnresolvedResolution;

// =============================================================================
// Auth + fetch primitives — mirror the apiFetch pattern from src/api/profit.ts
// but bind to API_CONFIG.taxBaseUrl.
// =============================================================================

async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit,
  baseUrl: string = API_CONFIG.taxBaseUrl
): Promise<T> {
  const url = `${baseUrl}${endpoint}`;

  if (baseUrl === 'http://localhost:3000') {
    throw new Error(
      'Tax API endpoint not configured. Set VITE_TAX_API_BASE_URL or VITE_TAX_WRITE_API_BASE_URL in your environment.'
    );
  }

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Tax API Error: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') throw new Error('Request timeout - please try again');
      throw error;
    }
    throw new Error('Unknown error occurred');
  }
}

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// =============================================================================
// Endpoint functions
// =============================================================================

export async function getTaxYears(): Promise<TaxYearsResponse> {
  return apiFetch<TaxYearsResponse>('/v1/tax/years');
}

export async function getPortfolios(taxYear: number): Promise<ListResponse<PortfolioOverviewItem>> {
  return apiFetch<ListResponse<PortfolioOverviewItem>>(
    `/v1/tax/portfolios${buildQuery({ taxYear })}`
  );
}

/**
 * Distinct entries from tax.portfolio_registry — the canonical source for
 * portfolio dropdown options. Independent of tax year and of which rows
 * happen to be on the current page of any specific view.
 */
export interface PortfolioRegistryItem {
  portfolio_id: string;
  display_name: string;
  environment: string;
  active: boolean;
}

export async function getPortfolioRegistry(): Promise<ListResponse<PortfolioRegistryItem>> {
  return apiFetch<ListResponse<PortfolioRegistryItem>>('/v1/tax/portfolio-registry');
}

export interface CoinbaseTransactionsParams extends PaginationParams, SortingParams {
  taxYear: number;
  portfolioId?: string;
  productId?: string;
}

export async function getCoinbaseTransactions(
  params: CoinbaseTransactionsParams
): Promise<ListResponse<CoinbaseTransactionItem>> {
  return apiFetch<ListResponse<CoinbaseTransactionItem>>(
    `/v1/tax/coinbase-transactions${buildQuery(params as unknown as Record<string, unknown>)}`
  );
}

export interface SystemTradesParams extends PaginationParams, SortingParams {
  taxYear: number;
  portfolioId?: string;
  productId?: string;
  tradeGroupType?: string;
}

export async function getSystemTrades(
  params: SystemTradesParams
): Promise<ListResponse<SystemTradeItem>> {
  return apiFetch<ListResponse<SystemTradeItem>>(`/v1/tax/system-trades${buildQuery(params as unknown as Record<string, unknown>)}`);
}

export interface ReconciliationParams extends PaginationParams, SortingParams {
  taxYear: number;
  portfolioId?: string;
  matchStatus?: 'MATCHED' | 'PARTIAL_MATCH' | 'UNMATCHED';
}

export async function getReconciliation(
  params: ReconciliationParams
): Promise<ListResponse<ReconciliationItem>> {
  return apiFetch<ListResponse<ReconciliationItem>>(`/v1/tax/reconciliation${buildQuery(params as unknown as Record<string, unknown>)}`);
}

export interface TaxLotsParams extends PaginationParams, SortingParams {
  taxYear: number;
  portfolioId?: string;
  productId?: string;
  sourceType?: 'COINBASE' | 'MANUAL';
}

export async function getTaxLots(params: TaxLotsParams): Promise<ListResponse<TaxLotItem>> {
  return apiFetch<ListResponse<TaxLotItem>>(`/v1/tax/tax-lots${buildQuery(params as unknown as Record<string, unknown>)}`);
}

export interface CostBasisParams extends PaginationParams, SortingParams {
  taxYear: number;
  portfolioId?: string;
  productId?: string;
  method?: 'SPECIFIC_ID' | 'HIFO' | 'LIFO' | 'FIFO' | 'ZERO_BASIS_UNKNOWN';
  holdingPeriod?: 'SHORT_TERM' | 'LONG_TERM';
}

export async function getCostBasis(params: CostBasisParams): Promise<ListResponse<CostBasisItem>> {
  return apiFetch<ListResponse<CostBasisItem>>(`/v1/tax/cost-basis${buildQuery(params as unknown as Record<string, unknown>)}`);
}

export interface MissingCostBasisParams extends PaginationParams, SortingParams {
  taxYear: number;
  portfolioId?: string;
}

export async function getMissingCostBasis(
  params: MissingCostBasisParams
): Promise<ListResponse<MissingCostBasisItem>> {
  return apiFetch<ListResponse<MissingCostBasisItem>>(
    `/v1/tax/missing-cost-basis${buildQuery(params as unknown as Record<string, unknown>)}`
  );
}

// =============================================================================
// Manual entries — read functions (taxBaseUrl) + write functions (taxWriteBaseUrl).
// Reads list/fetch manual transactions, manual tax lots, allocations, and
// Coinbase audit corrections. Writes create/update/delete manual entries and
// resolve inbox items (cost-basis disposals without a matching acquisition).
// =============================================================================

export async function getManualTransactions(
  params: Record<string, string | number | undefined>,
  signal?: AbortSignal
): Promise<ListResponse<ManualTransaction>> {
  return apiFetch<ListResponse<ManualTransaction>>(
    `/v1/tax/manual-transactions${buildQuery(params as Record<string, unknown>)}`,
    { signal }
  );
}

export async function getManualTransaction(
  id: number,
  signal?: AbortSignal
): Promise<SingleResponse<ManualTransaction>> {
  return apiFetch<SingleResponse<ManualTransaction>>(`/v1/tax/manual-transactions/${id}`, {
    signal,
  });
}

export async function getManualTaxLots(
  params: Record<string, string | number | undefined>,
  signal?: AbortSignal
): Promise<ListResponse<ManualTaxLot>> {
  return apiFetch<ListResponse<ManualTaxLot>>(
    `/v1/tax/manual-tax-lots${buildQuery(params as Record<string, unknown>)}`,
    { signal }
  );
}

export async function getManualTaxLot(
  id: number,
  signal?: AbortSignal
): Promise<SingleResponse<ManualTaxLot>> {
  return apiFetch<SingleResponse<ManualTaxLot>>(`/v1/tax/manual-tax-lots/${id}`, { signal });
}

export async function getAllocatedQuantity(
  portfolioId: string,
  acquisitionOrderId?: string,
  disposalOrderId?: string,
  signal?: AbortSignal
): Promise<AllocationResponse> {
  return apiFetch<AllocationResponse>(
    `/v1/tax/manual-tax-lots/allocation${buildQuery({ portfolioId, acquisitionOrderId, disposalOrderId })}`,
    { signal }
  );
}

export async function getCoinbaseCorrections(
  params: Record<string, string | number | undefined>,
  signal?: AbortSignal
): Promise<ListResponse<CoinbaseCorrection>> {
  return apiFetch<ListResponse<CoinbaseCorrection>>(
    `/v1/tax/coinbase-corrections${buildQuery(params as Record<string, unknown>)}`,
    { signal }
  );
}

// Write functions — route through taxWriteBaseUrl (separate API Gateway / Lambda).

export async function createManualTransaction(
  body: ManualTransactionBody,
  signal?: AbortSignal
): Promise<SingleResponse<ManualTransaction>> {
  return apiFetch<SingleResponse<ManualTransaction>>(
    '/v1/tax/manual-transactions',
    { method: 'POST', body: JSON.stringify(body), signal },
    API_CONFIG.taxWriteBaseUrl
  );
}

export async function updateManualTransaction(
  id: number,
  body: ManualTransactionUpdateBody,
  signal?: AbortSignal
): Promise<SingleResponse<ManualTransaction>> {
  return apiFetch<SingleResponse<ManualTransaction>>(
    `/v1/tax/manual-transactions/${id}`,
    { method: 'PUT', body: JSON.stringify(body), signal },
    API_CONFIG.taxWriteBaseUrl
  );
}

export async function deleteManualTransaction(
  id: number,
  reason: string,
  signal?: AbortSignal
): Promise<void> {
  return apiFetch<void>(
    `/v1/tax/manual-transactions/${id}`,
    { method: 'DELETE', body: JSON.stringify({ reason }), signal },
    API_CONFIG.taxWriteBaseUrl
  );
}

export async function createManualTaxLot(
  body: ManualTaxLotBody,
  signal?: AbortSignal
): Promise<SingleResponse<ManualTaxLot>> {
  return apiFetch<SingleResponse<ManualTaxLot>>(
    '/v1/tax/manual-tax-lots',
    { method: 'POST', body: JSON.stringify(body), signal },
    API_CONFIG.taxWriteBaseUrl
  );
}

export async function updateManualTaxLot(
  id: number,
  body: ManualTaxLotUpdateBody,
  signal?: AbortSignal
): Promise<SingleResponse<ManualTaxLot>> {
  return apiFetch<SingleResponse<ManualTaxLot>>(
    `/v1/tax/manual-tax-lots/${id}`,
    { method: 'PUT', body: JSON.stringify(body), signal },
    API_CONFIG.taxWriteBaseUrl
  );
}

export async function deleteManualTaxLot(
  id: number,
  reason: string,
  signal?: AbortSignal
): Promise<void> {
  return apiFetch<void>(
    `/v1/tax/manual-tax-lots/${id}`,
    { method: 'DELETE', body: JSON.stringify({ reason }), signal },
    API_CONFIG.taxWriteBaseUrl
  );
}

/**
 * Combined atomic inbox-resolution endpoint (REQ-003 / REQ-004 / REQ-005).
 * Creates the manual transaction (and optionally the manual tax lot for
 * UNASSIGNED_DISPOSAL) plus the inbox_resolution row in a single DB transaction.
 * Backend response uses singleEnvelope — typed apiFetch<{data, metadata}> here
 * and unwrapped to `data` per CLAUDE.md API standards.
 */
export async function resolveInboxItemCombined(
  payload: CombinedInboxResolutionPayload,
  signal?: AbortSignal
): Promise<InboxResolution> {
  const response = await apiFetch<{ data: InboxResolution; metadata: ResponseMetadata }>(
    '/v1/tax/inbox-resolutions/resolve',
    { method: 'POST', body: JSON.stringify(payload), signal },
    API_CONFIG.taxWriteBaseUrl
  );
  return response.data;
}
